/**
 * TELA 05 — CARRINHO
 * Exibição dos itens, escolhas, adicionais, observações, subtotal, taxa "A DEFINIR", total e edição.
 */

import { formatCurrency } from '../utils/formatters.js';
import { PRODUCTS } from '../data/menuData.js';

export function renderCartView(container, store) {
  const items = store.cart;
  const subtotal = store.getSubtotal();
  const deliveryFee = store.getDeliveryFee();
  const total = store.getTotal();
  const hasPendingItems = items.some(i => i.pricePending);

  if (items.length === 0) {
    container.innerHTML = `
      <div style="max-width: 580px; margin: 40px auto; text-align: center; padding: 48px 20px; background: var(--bg-surface); border-radius: var(--radius-xl); border: 1px dashed var(--border-subtle);">
        <div style="font-size: 3.5rem; margin-bottom: 12px;">🛒</div>
        <h1 style="font-size: 1.5rem; font-weight: 800; color: var(--text-primary);">Seu carrinho está vazio</h1>
        <p style="font-size: 0.95rem; color: var(--text-muted); margin: 8px 0 24px 0;">
          Que tal dar uma olhada nos lanches, jantinhas, pizzas ou pratos do Comida di Buteco?
        </p>
        <button id="btn-browse-menu" class="btn btn-primary" style="padding: 12px 28px;">
          Explorar Cardápio
        </button>
      </div>
    `;

    container.querySelector('#btn-browse-menu')?.addEventListener('click', () => {
      store.setView('home');
    });
    return;
  }

  container.innerHTML = `
    <div style="max-width: 720px; margin: 20px auto;">
      <!-- Cabeçalho do Carrinho -->
      <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; padding-bottom: 12px; border-bottom: 1px solid var(--border-subtle);">
        <button id="btn-cart-back" class="btn btn-secondary btn-sm">
          <span>←</span> <span>Continuar Comprando</span>
        </button>
        <h1 style="font-size: 1.3rem; font-weight: 800; color: var(--text-primary);">Meu Carrinho</h1>
        <button id="btn-clear-cart" class="btn btn-outline btn-sm" style="color: var(--color-danger); border-color: rgba(239, 68, 68, 0.4);">
          Limpar
        </button>
      </div>

      <!-- Lista de Itens -->
      <div class="cart-items-list">
        ${items.map(item => `
          <div class="cart-item-card" data-cart-id="${item.cartItemId}">
            <div class="cart-item-header">
              <div>
                <h3 style="font-size: 1.05rem; font-weight: 700; color: var(--text-primary);">${item.name}</h3>
                <div class="cart-item-details" style="margin-top: 4px;">
                  ${item.sizeName ? `<span>📏 <strong>Tamanho:</strong> ${item.sizeName}</span>` : ''}
                  ${item.portionType ? `<span>🍽️ <strong>Porção:</strong> ${item.portionType === 'meia' ? 'Meia porção (60%)' : 'Inteira'}</span>` : ''}
                  ${item.selectedMeat ? `<span>🥩 <strong>Carne:</strong> ${item.selectedMeat}</span>` : ''}
                  ${item.selectedChoice1 ? `<span>🥗 <strong>Opção 1:</strong> ${item.selectedChoice1}</span>` : ''}
                  ${item.selectedChoice2 ? `<span>🥔 <strong>Opção 2:</strong> ${item.selectedChoice2}</span>` : ''}
                  ${item.selectedEdge ? `<span>🧀 <strong>Borda:</strong> ${item.selectedEdge}</span>` : ''}
                  ${item.selectedSauce ? `<span>🍟 <strong>Molho:</strong> ${item.selectedSauce}</span>` : ''}
                  ${item.flavor ? `<span>🥟 <strong>Sabor:</strong> ${item.flavor}</span>` : ''}
                  ${item.selectedAddons && item.selectedAddons.length > 0 ? `
                    <span>➕ <strong>Adicionais:</strong> ${item.selectedAddons.map(a => `${a.name} (+${formatCurrency(a.price)})`).join(', ')}</span>
                  ` : ''}
                  ${item.notes && item.notes.trim() ? `
                    <span style="color: var(--accent-gold-light); font-style: italic;">💬 <strong>Obs:</strong> "${item.notes.trim()}"</span>
                  ` : ''}
                </div>
              </div>

              <!-- Botão Excluir -->
              <button class="btn-remove-item" data-cart-id="${item.cartItemId}" title="Remover item" style="color: var(--text-muted); font-size: 1.2rem; padding: 4px;">
                🗑️
              </button>
            </div>

            <!-- Rodapé do Card do Item -->
            <div class="cart-item-footer">
              <div style="display: flex; align-items: center; gap: 10px;">
                <div class="qty-control">
                  <button class="qty-btn btn-item-minus" data-cart-id="${item.cartItemId}">−</button>
                  <input type="text" class="qty-input" value="${item.quantity}" readonly />
                  <button class="qty-btn btn-item-plus" data-cart-id="${item.cartItemId}">+</button>
                </div>

                <button class="btn btn-secondary btn-sm btn-edit-item" data-cart-id="${item.cartItemId}" style="font-size: 0.8rem; padding: 6px 10px;">
                  ✏️ Editar
                </button>
              </div>

              <div style="text-align: right;">
                <span style="font-size: 1.15rem; font-weight: 800; color: var(--accent-gold-light); font-family: var(--font-display);">
                  ${item.pricePending ? 'Preço sob confirmação' : formatCurrency(item.totalPrice)}
                </span>
              </div>
            </div>
          </div>
        `).join('')}
      </div>

      <!-- Resumo Financeiro Homologado -->
      <div class="cart-summary-box">
        <h3 style="font-size: 1rem; font-weight: 700; margin-bottom: 8px;">Resumo dos Valores</h3>
        
        <div class="summary-row">
          <span>Subtotal dos itens:</span>
          <strong style="color: var(--text-primary);">${formatCurrency(subtotal)}</strong>
        </div>

        <div class="summary-row">
          <span>Taxa de Entrega:</span>
          <strong style="color: var(--accent-gold-light); font-size: 0.9rem;">
            ${deliveryFee !== null ? formatCurrency(deliveryFee) : 'A DEFINIR'}
          </strong>
        </div>

        ${hasPendingItems ? `
          <div style="padding: 8px 12px; background: rgba(245, 158, 11, 0.1); border-radius: var(--radius-sm); font-size: 0.82rem; color: #fbbf24;">
            ℹ️ <em>Atenção:</em> Seu pedido contém pratos do Comida di Buteco com preço a confirmar com o estabelecimento no WhatsApp.
          </div>
        ` : ''}

        <div class="summary-row summary-total">
          <span>Total Estimado:</span>
          <span style="font-size: 1.4rem;">${formatCurrency(total)}</span>
        </div>

        <button id="btn-proceed-checkout" class="btn btn-primary btn-block" style="margin-top: 14px; padding: 14px; font-size: 1.05rem;">
          Continuar para Finalização →
        </button>
      </div>
    </div>
  `;

  // Listeners
  container.querySelector('#btn-cart-back')?.addEventListener('click', () => {
    store.setView('home');
  });

  container.querySelector('#btn-clear-cart')?.addEventListener('click', () => {
    if (confirm("Deseja realmente esvaziar o seu carrinho?")) {
      store.clearCart();
    }
  });

  container.querySelectorAll('.btn-item-minus').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const cartId = e.currentTarget.getAttribute('data-cart-id');
      store.updateItemQuantity(cartId, -1);
    });
  });

  container.querySelectorAll('.btn-item-plus').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const cartId = e.currentTarget.getAttribute('data-cart-id');
      store.updateItemQuantity(cartId, 1);
    });
  });

  container.querySelectorAll('.btn-remove-item').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const cartId = e.currentTarget.getAttribute('data-cart-id');
      store.removeFromCart(cartId);
    });
  });

  // Editar Item
  container.querySelectorAll('.btn-edit-item').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const cartId = e.currentTarget.getAttribute('data-cart-id');
      const item = store.cart.find(i => i.cartItemId === cartId);
      if (!item) return;

      if (item.isJantinha) {
        store.setView('jantinha', { editingCartItemId: cartId });
      } else {
        const product = PRODUCTS.find(p => p.id === item.productId);
        if (product) {
          store.setView('product', { product, editingCartItemId: cartId });
        }
      }
    });
  });

  container.querySelector('#btn-proceed-checkout')?.addEventListener('click', () => {
    store.setView('checkout');
  });
}
